import type { Quat, Vec3 } from '../core/math'
import type { ShipDef } from '../data/schemas'

/** Pilot intent, each axis in [-1, 1]. Player input and AI both produce this (D-013). */
export interface ControlInput {
  /** +1 = forward (local −Z) */
  surge: number
  /** +1 = right (local +X) */
  sway: number
  /** +1 = up (local +Y) */
  heave: number
  /** +1 = nose up */
  pitch: number
  /** +1 = nose right */
  yaw: number
  /** +1 = roll right (clockwise seen from behind) */
  roll: number
}

/** World-space body state the flight model reads. */
export interface FlightBody {
  vel: Vec3
  angVel: Vec3
  rot: Quat
}

/** World-space output, applied to the Rapier body by the caller. */
export interface FlightForces {
  force: Vec3
  torque: Vec3
}

/** A float triple with its own map (a class, not a literal: `{ x, y, z }` literals share one map game-wide). */
class Vec3Scratch implements Vec3 {
  x = 0
  y = 0
  z = 0
}

/** The `ShipDef` fields the flight model reads (a `ShipDef` is one). */
export type FlightDef = Pick<
  ShipDef,
  | 'mass'
  | 'inertiaScale'
  | 'thrustForward'
  | 'thrustReverse'
  | 'thrustLateral'
  | 'thrustVertical'
  | 'maxSpeed'
  | 'absMaxSpeed'
  | 'boostSpeedMult'
  | 'boostThrustMult'
  | 'faGain'
  | 'rotGain'
  | 'maxRate'
  | 'maxAngAccel'
>

class AxisScratch {
  pitch = 0
  yaw = 0
  roll = 0
}

/**
 * A `FlightDef` with one map of its own (D-1118). Ship defs come out of the schema with different key sets (a
 * gunship has `turrets`), so `computeFlightForces` called with several defs saw polymorphic loads and boxed floats
 * (~12 B per call, measured); the hot caller copies the def into one of these each call (`loadFlightDef`), so the
 * values are always current and the reads monomorphic.
 */
export class FlightDefScratch implements FlightDef {
  mass = 0
  inertiaScale = 0
  thrustForward = 0
  thrustReverse = 0
  thrustLateral = 0
  thrustVertical = 0
  maxSpeed = 0
  absMaxSpeed = 0
  boostSpeedMult = 0
  boostThrustMult = 0
  faGain = 0
  rotGain = 0
  readonly maxRate = new AxisScratch()
  readonly maxAngAccel = new AxisScratch()
}

/** Copies `def`'s flight fields into `out` and returns it (same numbers; no allocation). */
export function loadFlightDef(def: FlightDef, out: FlightDefScratch): FlightDefScratch {
  out.mass = def.mass
  out.inertiaScale = def.inertiaScale
  out.thrustForward = def.thrustForward
  out.thrustReverse = def.thrustReverse
  out.thrustLateral = def.thrustLateral
  out.thrustVertical = def.thrustVertical
  out.maxSpeed = def.maxSpeed
  out.absMaxSpeed = def.absMaxSpeed
  out.boostSpeedMult = def.boostSpeedMult
  out.boostThrustMult = def.boostThrustMult
  out.faGain = def.faGain
  out.rotGain = def.rotGain
  out.maxRate.pitch = def.maxRate.pitch
  out.maxRate.yaw = def.maxRate.yaw
  out.maxRate.roll = def.maxRate.roll
  out.maxAngAccel.pitch = def.maxAngAccel.pitch
  out.maxAngAccel.yaw = def.maxAngAccel.yaw
  out.maxAngAccel.roll = def.maxAngAccel.roll
  return out
}

// Module scratch: no per-call allocation (D-014).
const vLocal = new Vec3Scratch()
const wLocal = new Vec3Scratch()
/** The vector `rotateInto` rotates. */
const vIn = new Vec3Scratch()

/**
 * `out` = `vIn` rotated by `q` (`inverse`: by its conjugate), the arithmetic of `core/math`'s `rotateRaw` bit for bit
 * (D-014). Only objects and a boolean cross the call: `computeFlightForces` runs past V8's cumulative inlining budget
 * part-way through its five rotations, and the seven floats passed to each un-inlined `rotateRaw` were boxed.
 */
function rotateInto(q: Quat, inverse: boolean, out: Vec3): void {
  const qx = inverse ? -q.x : q.x
  const qy = inverse ? -q.y : q.y
  const qz = inverse ? -q.z : q.z
  const qw = q.w
  const vx = vIn.x
  const vy = vIn.y
  const vz = vIn.z
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  out.x = vx + qw * tx + (qy * tz - qz * ty)
  out.y = vy + qw * ty + (qz * tx - qx * tz)
  out.z = vz + qw * tz + (qx * ty - qy * tx)
}

/**
 * Newtonian 6DOF flight with optional flight assist (spec §5, D-021).
 * FA on: a P-controller drives local velocity toward stick × maxSpeed, saturating at per-axis thrust.
 * FA off: the stick maps straight to thrust. Rotation is always rate-assisted.
 * A governor holds speed at absMaxSpeed in every mode; above it (after a collision) it brakes within thrust limits.
 * ponytail: P-only controller; add I/D terms only if tuning shows steady-state error or oscillation.
 */
export function computeFlightForces(
  body: FlightBody,
  control: ControlInput,
  def: FlightDef,
  faOn: boolean,
  boosting: boolean,
  dt: number,
  out: FlightForces,
): void {
  const m = def.mass
  const surge = boosting ? 1 : control.surge
  vIn.x = body.vel.x
  vIn.y = body.vel.y
  vIn.z = body.vel.z
  rotateInto(body.rot, true, vLocal)

  const fwdLimit = (def.thrustForward * (boosting ? def.boostThrustMult : 1)) / m
  const revLimit = def.thrustReverse / m
  const latLimit = def.thrustLateral / m
  const vertLimit = def.thrustVertical / m

  let ax: number
  let ay: number
  let az: number
  if (faOn) {
    const fwdSpeed = def.maxSpeed * (boosting ? def.boostSpeedMult : 1)
    const tz = surge >= 0 ? -surge * fwdSpeed : -surge * def.maxSpeed
    ax = def.faGain * (control.sway * def.maxSpeed - vLocal.x)
    ay = def.faGain * (control.heave * def.maxSpeed - vLocal.y)
    az = def.faGain * (tz - vLocal.z)
  } else {
    ax = control.sway * latLimit
    ay = control.heave * vertLimit
    az = surge >= 0 ? -surge * fwdLimit : -surge * revLimit
  }
  // The clamps are written out (`core/math`'s `clamp`, same expression): past the inlining budget a `clamp` call
  // boxed its float arguments and result (D-014). The rotation clamps below repeat their loads and products on purpose:
  // hoisted into consts they read 32 B per call again (measured, `flightModel.alloc.test.ts`).
  ax = ax < -latLimit ? -latLimit : ax > latLimit ? latLimit : ax
  ay = ay < -vertLimit ? -vertLimit : ay > vertLimit ? vertLimit : ay
  az = az < -fwdLimit ? -fwdLimit : az > revLimit ? revLimit : az // −Z (forward) uses forward thrust; +Z uses reverse thrust
  vIn.x = ax * m
  vIn.y = ay * m
  vIn.z = az * m
  rotateInto(body.rot, false, out.force)

  // Governor: project next-tick velocity back onto the absMaxSpeed sphere, then re-clamp the
  // corrective acceleration to each axis's thrust limit. Far above the cap (a collision bounce or
  // knockback) this brakes at full thrust over several ticks instead of one impossible impulse.
  const nvx = body.vel.x + (out.force.x / m) * dt
  const nvy = body.vel.y + (out.force.y / m) * dt
  const nvz = body.vel.z + (out.force.z / m) * dt
  const nextSpeed = Math.sqrt(nvx * nvx + nvy * nvy + nvz * nvz)
  if (nextSpeed > def.absMaxSpeed) {
    const s = def.absMaxSpeed / nextSpeed
    vIn.x = (nvx * s - body.vel.x) / dt
    vIn.y = (nvy * s - body.vel.y) / dt
    vIn.z = (nvz * s - body.vel.z) / dt
    rotateInto(body.rot, true, vLocal)
    ax = vLocal.x
    ay = vLocal.y
    az = vLocal.z
    ax = ax < -latLimit ? -latLimit : ax > latLimit ? latLimit : ax
    ay = ay < -vertLimit ? -vertLimit : ay > vertLimit ? vertLimit : ay
    az = az < -fwdLimit ? -fwdLimit : az > revLimit ? revLimit : az
    vIn.x = ax * m
    vIn.y = ay * m
    vIn.z = az * m
    rotateInto(body.rot, false, out.force)
  }

  // Rotation. +yaw (nose right) and +roll (roll right) are negative rotations about +Y and +Z.
  vIn.x = body.angVel.x
  vIn.y = body.angVel.y
  vIn.z = body.angVel.z
  rotateInto(body.rot, true, wLocal)
  const inertia = m * def.inertiaScale
  const tx = control.pitch * def.maxRate.pitch
  const ty = -control.yaw * def.maxRate.yaw
  const tz = -control.roll * def.maxRate.roll
  const alx = def.rotGain * (tx - wLocal.x) < -def.maxAngAccel.pitch ? -def.maxAngAccel.pitch : def.rotGain * (tx - wLocal.x) > def.maxAngAccel.pitch ? def.maxAngAccel.pitch : def.rotGain * (tx - wLocal.x)
  const aly = def.rotGain * (ty - wLocal.y) < -def.maxAngAccel.yaw ? -def.maxAngAccel.yaw : def.rotGain * (ty - wLocal.y) > def.maxAngAccel.yaw ? def.maxAngAccel.yaw : def.rotGain * (ty - wLocal.y)
  const alz = def.rotGain * (tz - wLocal.z) < -def.maxAngAccel.roll ? -def.maxAngAccel.roll : def.rotGain * (tz - wLocal.z) > def.maxAngAccel.roll ? def.maxAngAccel.roll : def.rotGain * (tz - wLocal.z)
  vIn.x = alx * inertia
  vIn.y = aly * inertia
  vIn.z = alz * inertia
  rotateInto(body.rot, false, out.torque)
}
